/**
 * Debug Cache Button - Dev Mode Only
 * Shows React Query cache state for verification
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bug, X } from 'lucide-react';

interface QueryState {
  queryKey: unknown;
  status: string;
  dataUpdatedAt: number;
  isFetching: boolean;
  dataUpdatedAtISO: string;
  staleTime: string;
}

export default function DebugCache() {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  // Only render in dev mode
  if (!import.meta.env.DEV) {
    return null;
  }

  const getQueryStates = (): QueryState[] => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    return queries.map((query) => {
      const state = query.state;
      return {
        queryKey: query.queryKey,
        status: state.status,
        dataUpdatedAt: state.dataUpdatedAt,
        dataUpdatedAtISO: state.dataUpdatedAt 
          ? new Date(state.dataUpdatedAt).toISOString() 
          : 'never',
        isFetching: state.fetchStatus === 'fetching',
        staleTime: `${Math.round((Date.now() - state.dataUpdatedAt) / 1000)}s ago`,
      };
    });
  };

  const [queryStates, setQueryStates] = useState<QueryState[]>([]);

  const refreshStates = () => {
    const states = getQueryStates();
    setQueryStates(states);
    console.info('[DebugCache] Query States:', JSON.stringify(states, null, 2));
  };

  return (
    <>
      {/* Debug Button */}
      <button
        onClick={() => {
          setIsOpen(true);
          refreshStates();
        }}
        className="fixed bottom-4 left-4 z-50 bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition"
        title="Debug Cache"
      >
        <Bug className="w-5 h-5" />
      </button>

      {/* Debug Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold">🔍 Debug Cache (Dev Only)</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={refreshStates}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Refresh
                </button>
                <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <p className="text-sm text-gray-500 mb-4">
                Current Time: {new Date().toISOString()}
              </p>

              {queryStates.length === 0 ? (
                <p className="text-gray-500">No queries in cache</p>
              ) : (
                <div className="space-y-3">
                  {queryStates.map((q, idx) => (
                    <div key={idx} className="bg-gray-50 p-3 rounded-lg border text-sm font-mono">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          q.status === 'success' ? 'bg-green-100 text-green-700' :
                          q.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {q.status}
                        </span>
                        {q.isFetching && (
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700 animate-pulse">
                            fetching
                          </span>
                        )}
                      </div>
                      <p><strong>queryKey:</strong> {JSON.stringify(q.queryKey)}</p>
                      <p><strong>dataUpdatedAt:</strong> {q.dataUpdatedAtISO}</p>
                      <p><strong>staleTime:</strong> {q.staleTime}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50">
              <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(queryStates, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
